import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import { useTranslations } from '../../contexts/translationContext';
import TranslationModel from '../../models/TranslationModel';
import DataService from '../../services/data.service';
import { Result } from '../../types/Result';
import Button from './../../components/Buttons/Button';
import ButtonBar from './../../components/Fields/ButtonBar';
import Form from './../../components/Fields/Form';
import SelectListField from './../../components/Fields/SelectListField';
import TextboxField from './../../components/Fields/TextboxField';
import Unauthorized from './../Authentication/Unauthorized';

interface TranslationSearchCriteria {
    translationKey?: string
    translatedText?: string
    language?: string
    context?: string
    unverifiedOnly: boolean
    unapprovedOnly: boolean
    unfinalizedOnly: boolean
}

const TranslationsSearchView: React.FC = () => {
    const [tableData, setTableData] = useState<TranslationModel[]>([])
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleResult, handleException } = useNotifications();
    const { isAuthorizedFor, user } = useAuth()
    const { getContext } = useTranslations()

    const [formData, setFormData] = useState<TranslationSearchCriteria>({
        language: user?.language,
        context: getContext(), 
        unverifiedOnly: false,
        unapprovedOnly: false,
        unfinalizedOnly: false,
    });

    const languages = useSelectListOptions('api/v1/selectlist/Languages', '-- All languages');
    const contexts = useSelectListOptions('api/v1/selectlist/Contexts', '-- All contexts');

    useEffect(() => {
        const filterItem = localStorage.getItem("translationsFilter");
        if (filterItem !== null) {
            let filter: TranslationSearchCriteria = JSON.parse(filterItem);
            setFormData(x => ({ ...filter }));
        }
    }, [])

    function search() {
        setShowSpinner(true);
        localStorage.setItem('translationsFilter', JSON.stringify(formData));
        DataService
            .post<TranslationModel[]>(`api/v1/Translations/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setTableData(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e)
            });
    }

    function verifyRow(rowData) {
        DataService.post<Result>(`api/v1/Translations/${rowData.id}/Verify`, rowData)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function approveRow(rowData) {
        DataService.post<Result>(`api/v1/Translations/${rowData.id}/Approve`, rowData)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function finalizeRow(rowData) {
        DataService.post<Result>(`api/v1/Translations/${rowData.id}/Finalize`, rowData)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }
    function deleteRow(rowData) {
        DataService.delete('api/v1/Translations/', rowData.id)
            .then(response => {
                handleResult(response);
                search();
            })
            .catch(e => { handleException(e); });
    }

    function onCellChange(value: any, rowIndex: number, dataProp?: string) {
        if (tableData && dataProp) {
            dataProp.split('.').reduce((object, propertyName, index, elements) => {
                if (index === elements.length - 1) {
                    object[propertyName] = value;
                    setTableData(tableData.slice());
                }

                if (object && object[propertyName]) {
                    return object[propertyName];
                }
                return null;
            }, tableData[rowIndex]);
        }
    }

    function getStatusColor(rowData: TranslationModel, colInfo: IColInfo) : string|undefined {
        let color = "#ddd";
        if (rowData.verifiedOn !== undefined) { color = "green"; }
        if (rowData.approvedOn != undefined) { color = "#145A32"; }
        if (rowData.finalizedOn != undefined) { color = "Black"; }
        if (rowData.deletedOn != undefined) { color = "Red"; }

        return color
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'translationKey', label: 'Translation Key', labelSize: 2 },
            { name: 'translatedText', label: 'Translated Text', labelSize: 2 },
            { name: 'language', label: 'Language', labelSize: 2 },
            { name: 'context', label: 'Context', labelSize: 2 },
            { name: 'unverifiedOnly', label: 'Show unverified translations only', labelSize: 5 },
            { name: 'unapprovedOnly', label: 'Show unapproved translations only', labelSize: 5 },
            { name: 'unfinalizedOnly', label: 'Show unfinalized translations only', labelSize: 5 },
        ],
        handleSubmit: search,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);


    if (!isAuthorizedFor(ApplicationFunctions.ViewBusinessPartner)) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Translations' footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('translationKey')} />
                            <TextboxField {...getFieldProps('translatedText')} />
                            <SelectListField {...getFieldProps('language')} options={languages} />
                            <SelectListField {...getFieldProps('context')} options={contexts} />
                        </Col>
                        <Col md='6'>
                            <SwitchField {...getFieldProps('unverifiedOnly')} />
                            <SwitchField {...getFieldProps('unapprovedOnly')} />
                            <SwitchField {...getFieldProps('unfinalizedOnly')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            <div className="card" style={{ margin: 12 }}>
                <DataTable
                    name='dtTranslations'
                    tableData={tableData}
                    pageSize={50}
                    onCellChange={onCellChange}
                    colInfos={[
                        { key: true, dataProp: 'id', visible: false },
                        { getCellBackgroundColor: getStatusColor, sort: false, width: 5 },
                        { title: 'Language', dataProp: 'language', filterInfo: { filterType: 'CheckboxList' } },
                        { title: 'Context', dataProp: 'context', filterInfo: { filterType: 'Textbox' } },
                        { title: 'Translation key', dataProp: 'translationKey', filterInfo: { filterType: 'Textbox' } },
                        { title: 'Translated text', dataProp: 'translatedText', editable: true, filterInfo: { filterType: 'Textbox' } },
                        { title: 'Used on', dataProp: 'lastAccessedOn', dataType: 'datetime' },
                        {
                            title: 'Verify',
                            sort: false,
                            rowButtons: [{ text: "Verify", icon: 'fa-check', color: 'green', onClick: verifyRow, applicationFunctions: [ApplicationFunctions.VerifyTranslation] }],
                        },
                        {
                            title: 'Approve',
                            sort: false,
                            rowButtons: [{ text: "Approve", icon: 'fa-thumbs-up', color: '#145A32', onClick: approveRow, applicationFunctions: [ApplicationFunctions.ApproveTranslation] }],
                        },
                        {
                            title: 'Finalize',
                            sort: false,
                            rowButtons: [{ text: "Finalize", icon: 'fa-gift', color: 'black', onClick: finalizeRow, applicationFunctions: [ApplicationFunctions.ApproveTranslation] }],
                        },
                        {
                            title: 'Delete',
                            sort: false,
                            rowButtons: [{ text: "Delete", icon: 'fa-trash', color: '#F3565D', onClick: deleteRow, applicationFunctions: [ApplicationFunctions.ApproveTranslation] }],
                        },
                    ]}
                />
            </div>
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default TranslationsSearchView;